/**
 * Komponente für Eingabe und Auswertung einer Gas-Komponente für die Kalibrierung eines 4-Gas Testers
 */

import { observable, computed, action } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { bind, inject } from '../../../utils/decorators';
import { FormControl, InputGroup } from 'react-bootstrap';
import { Agt4GasKalibrierungStore } from '../../../stores/Agt4GasKalibrierungStore';
import { IArbeitsmittel, IObjektVorlage } from '../../../models/ApiTypes';
import { round } from '../../../utils/round';
import { numberformatFixed } from '../../../utils/numberformat';

interface IFehlergrenze {
  absolut: number;
  relativ: number;
}

interface IAgt4GasMessungKomponenteProps {
  genauigkeitsklasse: string;
  beschriftung: string;
  einheit: string;
  bezeichner: string;
  propanwertverwenden: boolean;
  propanhexanfaktor: number | undefined;
  formdataprefix: string;
  value?: string;
  arbeitsmittel: IArbeitsmittel | undefined;
}

@observer
export default class Agt4GasxMessungKomponente extends React.Component<IAgt4GasMessungKomponenteProps, {}> {
  @inject(Agt4GasKalibrierungStore) kalibrierungStore: Agt4GasKalibrierungStore;

  fehlergrenzen: { [genauigkeitsklasse: string]: IFehlergrenze; } = {};

  @observable istEingabe: string | undefined;
  @observable sollwertGespeichert: number | undefined;

  constructor(props: IAgt4GasMessungKomponenteProps) {
    super(props);

    // Abbildung der absoluten und relativen Fehlergrenzen aus OIML R99
    switch (this.props.bezeichner) {
      case 'komponente_co':
        this.fehlergrenzen['00'] = { absolut: 0.02, relativ: 5 };
        this.fehlergrenzen['0'] = { absolut: 0.03, relativ: 5 };
        this.fehlergrenzen['1'] = { absolut: 0.06, relativ: 5 };
        break;
      case 'komponente_co2':
        this.fehlergrenzen['00'] = { absolut: 0.3, relativ: 5 };
        this.fehlergrenzen['0'] = { absolut: 0.5, relativ: 5 };
        this.fehlergrenzen['1'] = { absolut: 0.5, relativ: 5 };
        break;
      case 'komponente_hc':
        this.fehlergrenzen['00'] = { absolut: 4, relativ: 5 };
        this.fehlergrenzen['0'] = { absolut: 10, relativ: 5 };
        this.fehlergrenzen['1'] = { absolut: 12, relativ: 5 };
        break;
      case 'komponente_o2':
        this.fehlergrenzen['00'] = { absolut: 0.1, relativ: 5 };
        this.fehlergrenzen['0'] = { absolut: 0.1, relativ: 5 };
        this.fehlergrenzen['1'] = { absolut: 0.1, relativ: 5 };
        break;
      default:
        this.fehlergrenzen['00'] = { absolut: 0, relativ: 0 };
        this.fehlergrenzen['0'] = { absolut: 0, relativ: 0 };
        this.fehlergrenzen['1'] = { absolut: 0, relativ: 0 };
        break;
    }

    // Im Vorgang bereits hinterlegte Werte übernehmen
    if (this.props.value) {
      if (props.value) {
        let obj = JSON.parse(props.value);
        if (this.props.bezeichner === 'komponente_co' && obj.co) {
          this.setWerte(obj.co.soll && obj.co.soll, obj.co.ist && obj.co.ist);
        }
        if (this.props.bezeichner === 'komponente_co2' && obj.co2) {
          this.setWerte(obj.co2.soll && obj.co2.soll, obj.co2.ist && obj.co2.ist);
        }
        if (this.props.bezeichner === 'komponente_hc' && obj.hc) {
          this.setWerte(obj.hc.soll && obj.hc.soll, obj.hc.ist && obj.hc.ist);
        }
        if (this.props.bezeichner === 'komponente_o2' && obj.o2) {
          this.setWerte(obj.o2.soll && obj.o2.soll, obj.o2.ist && obj.o2.ist);
        }
      }
    }
  }

  private findMeta(bezeichnung: string) {
    if (this.props.arbeitsmittel && this.props.arbeitsmittel.meta) {
      let metadatum = this.props.arbeitsmittel.meta.find((meta) => meta.bezeichnung.toLocaleLowerCase() === bezeichnung);
      if (metadatum) {
        return metadatum.inhalt;
      }
    }
    return undefined;
  }

  @bind public setWerte(soll: number | undefined, ist: string | undefined) {
    this.istEingabe = ist ? ist : undefined;
    this.sollwertGespeichert = soll;
  }

  @bind @action handleIstWertChange(ev: React.FormEvent<FormControl>) {
    if ((ev.target as HTMLInputElement).value) {
      let match = (ev.target as HTMLInputElement).value.match(/-?(\d{0,}.)?(\d{0,},)?\d{0,}/g);
      this.istEingabe = match ? match.join('') : undefined;
    } else {
      this.istEingabe = undefined;
    }
  }

  /**
   * Der Event Handler für das Verlassen des Eingabefeldes Istwert kümmert sich um die Verlängerung oder Verkürzung
   * des Ist-Wertes entsprechend der im Sollwert definierten Nachkommastellen
   */
  @bind @action handleIstWertOnBlur() {
    const trenner = '.';
    if (this.istwert !== undefined && this.nachkommaGenauigkeit !== undefined) {
      if (this.istEingabe) {
        let nachkommaTeil = this.istEingabe.indexOf(trenner) > -1 ? this.istEingabe.split(trenner).pop() : '';
        let nachkommaLen = nachkommaTeil ? nachkommaTeil.length : 0;
        if (nachkommaLen < this.nachkommaGenauigkeit) {
          if (this.istEingabe.indexOf(trenner) === -1) {
            this.istEingabe += trenner;
          }
          this.istEingabe = this.istEingabe + '0'.repeat(this.nachkommaGenauigkeit - nachkommaLen);
        }
        if (this.istwert && nachkommaLen > this.nachkommaGenauigkeit) {
          this.istEingabe = round(parseFloat(this.istEingabe), this.nachkommaGenauigkeit).toString();
        }
      }
    }
  }

  /**
   * Berechnet die für die gesetzte Genauigkeitsklasse des AGT anzuwendende Fehlergrenze
   */
  @computed get fehlergrenze() {
    let fehlergrenze = this.fehlergrenzen[this.props.genauigkeitsklasse];
    return fehlergrenze;
  }

  /**
   * Berechnet den anzuwendenden Ist-Wert aus der Nutzer-Eingabe
   */
  @computed get istwert() {
    if (this.istEingabe === undefined) {
      return undefined;
    }
    let floatVal = parseFloat(this.istEingabe.replace(',', '.'));
    if (floatVal === NaN) {
      return undefined;
    }
    if ((this.props.bezeichner === 'komponente_hc') && (this.props.propanwertverwenden)) {
      // Bei Hexan/Propan je nachdem ob Umrechnung aktiv ist, Ist-Wert durch PEF teilen
      return floatVal / (this.props.propanhexanfaktor ? this.props.propanhexanfaktor : 1.0);
    } else {
      return floatVal;
    }
  }

  /**
   * Berechnet die zu der chemischen Komponente passende Messunsicherheit anhand der in der Objektvorlage hinterlegten Eigenschaften
   */
  @computed get messunsicherheit() {
    let arbeitsmittel: IArbeitsmittel | undefined = this.props.arbeitsmittel;
    let objektvorlage: IObjektVorlage | undefined = this.kalibrierungStore.objektVorlage;
    if (arbeitsmittel && objektvorlage) {
      let bezeichner: string = this.props.bezeichner === 'komponente_hc' ? 'komponente_propan' : this.props.bezeichner.toLowerCase();
      for (let meta of objektvorlage.meta) {
        if (meta.kategorie === 'messunsicherheit' && meta.detail === arbeitsmittel.typ.toLocaleLowerCase() && meta.eigenschaft === bezeichner) {
          return parseFloat(meta.wert);
        }
      }
    }
    return undefined;
  }

  /**
   * Berechnet den Toleranzbereich für die Komponente aus der OIMLR R99 Tabelle, optional unter Einbezug der Messunsicherheit
   */
  @computed get toleranzbereich() {
    if (this.sollwert === undefined) {
      return 0.0;
    }
    // Zur Genauigkeitsklasse des AGT passende Fehlergrenze ermitteln
    const zutreffendeFehlergrenze = this.fehlergrenzen[this.props.genauigkeitsklasse];
    if (zutreffendeFehlergrenze) {
      // Lt. Richtilinie gilt immer der jeweils größere Wert zwischen relativer und absoluter Toleranzgrenze
      let bereich = 0.0;
      let toleranzRelativ = this.sollwert * (zutreffendeFehlergrenze.relativ / 100.0);
      bereich = toleranzRelativ > zutreffendeFehlergrenze.absolut ? toleranzRelativ : zutreffendeFehlergrenze.absolut;

      // Die Nutzung der Messunsicherheit ist optional, und wird nur auf den Toleranzbereich angewendet wenn explizit gewünscht
      if (this.kalibrierungStore.messunsicherheitAnwenden && this.messunsicherheit) {
        bereich -= this.messunsicherheit;
      }
      return bereich;
    } else {
      return 0.0;
    }
  }

  /**
   * Berechnet den zu der chemischen Komponenten passenden Sollwert aus dem gewählten Arbeitsmittel
   */
  @computed get sollwert() {
    let bezeichner: string = this.props.bezeichner === 'komponente_hc' ? 'komponente_propan' : this.props.bezeichner.toLowerCase();
    let metaSoll = this.findMeta('gemisch_' + bezeichner);
    if (metaSoll) {
      return parseFloat(metaSoll);
    }
    // Bei Prüfgasen != O2 muss die Komponente O2 immer 0,00% betragen, es ist also u.U. kein passendes Meta-Datum vorhanden
    if (this.props.arbeitsmittel && (this.props.bezeichner === 'komponente_o2')) {
      return 0.0;
    }
    // Wenn kein Arbeitsmittel (mehr) gewählt, wird, falls vorhanden, der im Datensatz gespeicherte Sollwert zurückgegeben
    return this.sollwertGespeichert;
  }

  /**
   * Nachkomma-Anteil Sollwert für DAkkS-konforme Betrachtung der Messwerte
   * @todo: Elegantere Lösung (evtl. durch Umstellung auf zentralen Store wie beim Verfahren BPS)
   */
  @computed get nachkommaGenauigkeit() {
    let bezeichner: string = this.props.bezeichner === 'komponente_hc' ? 'komponente_propan' : this.props.bezeichner.toLowerCase();
    let metaSoll = this.findMeta('gemisch_' + bezeichner);
    if (metaSoll) {
      let nachkommaTeil = metaSoll.indexOf('.') > -1 ? metaSoll.split('.').pop() : '';
      if (nachkommaTeil) {
        return nachkommaTeil.length;
      }
    }
    // Bei Prüfgasen != O2 muss die Komponente O2 immer 0,00% betragen, es ist also u.U. kein passendes Meta-Datum vorhanden
    if (this.props.bezeichner === 'komponente_o2') {
      return 2;
    }
    return 0;
  }

  /**
   * Berechnet ob der anzuwendende Ist-Wert im über Fehlergrenze und Messunsucherheit ermitteltem Toleranzbereich liegt
   */
  @computed get imToleranzbereich() {
    if (this.istwert !== undefined && this.sollwert !== undefined) {
      return (this.istwert >=  this.sollwert - this.toleranzbereich) && (this.istwert <=  this.sollwert + this.toleranzbereich);
    } else {
      return undefined;
    }
  }

  public render() {
    let zeigeHCKorrigiert: boolean = this.props.bezeichner === 'komponente_hc' && this.props.propanwertverwenden;
    return (
      <tr key={`tr_${this.props.bezeichner}`}>
        <td className="komponente" style={{minWidth: '64px'}}>{this.props.beschriftung}</td>
        <td>
          <InputGroup>
            <FormControl
              type="text"
              readOnly={true}
              value={numberformatFixed(this.sollwert, this.nachkommaGenauigkeit)}
              tabIndex={-1}
            />
            <InputGroup.Addon style={{minWidth: '76px'}}>{this.props.einheit}</InputGroup.Addon>
            <FormControl type="hidden" name={`${this.props.formdataprefix}[soll]`} value={this.sollwert} />
            <FormControl type="hidden" name={`${this.props.formdataprefix}[genauigkeit]`} value={this.nachkommaGenauigkeit} />
          </InputGroup>
          {zeigeHCKorrigiert && <div style={{verticalAlign: 'inherit', fontSize: '14px', marginTop: '14px', textAlign: 'right'}}>Propan</div>}
        </td>
        <td>
          <InputGroup>
            <FormControl
              type="number"
              step="any"
              name={`${this.props.formdataprefix}[ist]`}
              onChange={this.handleIstWertChange}
              onBlur={this.handleIstWertOnBlur}
              readOnly={this.kalibrierungStore.readOnly}
              value={this.istEingabe ? this.istEingabe.replace(',', '.') : ''}
              className={this.imToleranzbereich === true ? 'text-success' : 'text-danger'}
              autoComplete="off"
            />
            <FormControl
              type="hidden"
              name={`${this.props.formdataprefix}[fehlergrenze][eingehalten]`}
              value={this.imToleranzbereich ? 'true' : 'false'}
            />
          </InputGroup>
          {/* Korrigierten Propanwert anzeigen wenn Umrechung aktiviert */}
          {zeigeHCKorrigiert &&
            <div style={{marginTop: '8px'}}>
              <InputGroup>
                <FormControl
                  type="number"
                  readOnly={true}
                  tabIndex={-1}
                  value={this.istwert ? round(this.istwert, 3) : ''}
                  className={this.imToleranzbereich === true ? 'text-success' : 'text-danger'}
                  style={{border: '0px', backgroundColor: 'inherit'}}
                />
                {/* Korrigierten Wert über unsichtbares Input an Back-End übergeben */}
                <FormControl
                  type="hidden"
                  name={`${this.props.formdataprefix}[ist_korrigiert]`}
                  value={this.istwert}
                />
              </InputGroup>
            </div>
          }
        </td>
        <td>
          <InputGroup>
            <InputGroup.Addon>±</InputGroup.Addon>
            <FormControl
              type="number"
              readOnly={true}
              value={round(this.toleranzbereich, 3)}
              tabIndex={-1}
            />
            {/* Toleranzbereich als unsichtbares Feld in Request übergeben */}
            <FormControl
              type="hidden"
              readOnly={true}
              name={`${this.props.formdataprefix}[toleranzbreich]`}
              value={this.toleranzbereich}
            />
            {/* Messunsicherheit als unsichtbares Feld in Request übergeben */}
            <FormControl
              type="hidden"
              readOnly={true}
              name={`${this.props.formdataprefix}[mu]`}
              value={this.messunsicherheit}
            />
            {/* Fehlergrenzen als unsichtbare Felder in Request übergeben */}
            <FormControl
              type="hidden"
              name={`${this.props.formdataprefix}[fehlergrenze][absolut]`}
              value={this.fehlergrenze.absolut}
            />
            <FormControl
              type="hidden"
              name={`${this.props.formdataprefix}[fehlergrenze][relativ]`}
              value={this.fehlergrenze.relativ}
            />
          </InputGroup>
        </td>
      </tr>
    );
  }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/vorlagen/agt/Agt4GasMessungKomponente.tsx